import { Box, styled, Typography, useMediaQuery } from '@mui/material'
import React, { useEffect, useState } from 'react'
import { theme, colors } from '../../theme'
import { FeaturesList } from './FeaturesList'
import { ChoosePaymentPlanModal } from './ChoosePaymentPlanModal'
import { StripeConnectButton } from '../../Adopters/StripeConnectButton'

const TabContainer = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  gap: '0.5rem',
  backgroundColor: theme.palette.background.default,
  zIndex: 2,
  position: 'relative',
  boxShadow: '4px 4px 4px 0px rgba(0, 0, 0, 0.25)',
  borderRadius: '30px',
  padding: '10px',
})

const TabButton = styled(Box)(({ selected }: { selected: boolean }) => ({
  padding: '0.75rem 1rem',
  borderRadius: '20px',
  fontWeight: 600,
  color: selected ? 'white' : theme.palette.secondary.main,
  backgroundColor: selected ? theme.palette.secondary.main : theme.palette.background.default,
  boxShadow: selected ? 'inset 0px 4px 4px 0px rgba(0, 0, 0, 0.25)' : 'none',
  cursor: 'pointer',
  transition: 'all 0.2s ease-in-out',
  '&:hover': {
    backgroundColor: selected ? theme.palette.secondary.light : theme.palette.action.hover,
  },
  whiteSpace: 'nowrap',
}))

const HorizontalLine = styled(Box)({
  width: '100%',
  height: '2px',
  backgroundColor: theme.palette.secondary.main,
  zIndex: 1,
})

const ContentCard = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  backgroundColor: theme.palette.background.paper,
  borderRadius: '8px',
  boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
  padding: '1.5rem',
}))

interface Props {
  isSubscribed: boolean
}

const OfferTabs: React.FC<Props> = ({ isSubscribed }) => {
  const [selectedTab, setSelectedTab] = useState<'free' | 'premium'>(isSubscribed ? 'premium' : 'free')
  const isMobile = useMediaQuery('(max-width:600px)')
  const [sticky, setSticky] = useState(false)

  useEffect(() => {
    if (!isMobile) {
      setSticky(false)
      return
    }
    const handleScroll = () => {
      const y = window.scrollY
      if (y > 120) setSticky(true)
      if (y < 60) setSticky(false)
    }
    window.addEventListener('scroll', handleScroll)
    handleScroll()
    return () => window.removeEventListener('scroll', handleScroll)
  }, [isMobile])

  const renderLines = (
    items: { label: string; available: boolean; emphasis?: boolean; secondary?: string }[],
    fontSize: string | { xs: string; md: string } = { xs: '0.9rem', md: '0.95rem' }
  ) => (
    <Box
      component="div"
      sx={{
        paddingLeft: { xs: '28px', md: '56px' },
        color: theme.palette.secondary.main,
        textAlign: 'left',
        fontWeight: 500,
        fontSize,
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
      }}
    >
      {items.map((item, index) => (
        <Box
          key={item.label}
          sx={{
            display: 'flex',
            gap: 0.5,
            alignItems: 'flex-start',
            lineHeight: { xs: 1.25, md: 1.2 },
          }}
        >
          <Box component="span" sx={{ minWidth: 18, textAlign: 'right' }}>
            {item.available ? `${index + 1}.` : '❌'}
          </Box>
          <Box component="span" sx={{ display: 'inline' }}>
            <Box
              component="span"
              sx={{
                fontWeight: item.emphasis ? 700 : 500,
                color: item.emphasis ? theme.palette.success.main : undefined,
              }}
            >
              {item.label}
            </Box>
            {item.secondary && (
              <Box component="span" sx={{ fontWeight: 500, color: theme.palette.secondary.main }}>
                {item.secondary}
              </Box>
            )}
          </Box>
        </Box>
      ))}
    </Box>
  )

  return (
    <Box sx={{ maxWidth: '100%', margin: 'auto', textAlign: 'center', mt: 4 }}>
      <Box
        sx={{
          width: '100%',
          position: sticky ? 'sticky' : 'relative',
          top: sticky ? 0 : 'auto',
          zIndex: sticky ? 20 : 'auto',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          gap: 2,
          backgroundColor: sticky ? theme.palette.background.default : 'transparent',
          py: sticky ? 1 : 0,
        }}
      >
        <HorizontalLine />
        <TabContainer>
          <TabButton selected={selectedTab === 'free'} onClick={() => setSelectedTab('free')}>
            Offre gratuite
          </TabButton>
          <TabButton selected={selectedTab === 'premium'} onClick={() => setSelectedTab('premium')}>
            48€/Mois TTC
          </TabButton>
        </TabContainer>
        <HorizontalLine />
      </Box>

      <ContentCard sx={{ mt: 4, mb: 3, flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', flexDirection: { md: 'row', xs: 'column' }, justifyContent: 'space-between' }}>
          <Box sx={{ width: { md: '50%', xs: '100%' } }}>
            <FeaturesList title="Suivi sanitaire et administratif">
              {renderLines([
                { label: 'Création et édition de la fiche animale détaillée', available: true },
                { label: 'Suivi et rappel automatique des soins et rendez-vous', available: true },
                { label: 'Gestion de boxes et suivi des déplacements', available: selectedTab !== 'free' },
              ])}
            </FeaturesList>

            <FeaturesList title="Suivi des adoptions">
              {renderLines(
                [
                  { label: 'Création de vos modèles (certificats et contrats)', available: selectedTab !== 'free' },
                  {
                    label: 'Transmission et signatures électroniques des documents',
                    available: selectedTab !== 'free',
                  },
                  { label: 'facultatif : réglements des frais d’adoption en ligne', available: true },
                ],
                '0.9rem'
              )}
            </FeaturesList>

            <FeaturesList title="Gestion des membres">
              {renderLines([
                {
                  label: 'Nombre de licences illimitées',
                  secondary: ' (selon 4 niveaux d’accès)',
                  available: true,
                  emphasis: true,
                },
                { label: 'Assignation des animaux à un responsable/une FA', available: true },
                { label: 'Gestion des donateurs et des adhérents & reçus fiscaux', available: selectedTab !== 'free' },
              ])}
            </FeaturesList>
          </Box>

          <Box sx={{ width: { md: '50%', xs: '100%' } }}>
            <FeaturesList title="Gestion des annonces">
              {renderLines([
                { label: 'Gestion des statuts d’adoption', available: true },
                { label: 'Création de votre page association sur Petso.fr', available: true },
                { label: 'Publication automatiques auprès de sites partenaires', available: true },
              ])}
            </FeaturesList>

            <FeaturesList title="Carnet de santé digital pour vos adoptants">
              {renderLines(
                [
                  { label: 'Transmission du profil de l’animal et des rappels à venir', available: true },
                  { label: 'Option de rappel et validation de la stérilisation', available: true },
                  { label: 'Conseils & recommandations post-adoption', available: true },
                ],
                '0.9rem'
              )}
            </FeaturesList>

            <FeaturesList title="Exports et suivi global">
              {renderLines([
                { label: 'Génération du E/S, sanitaire et bons vétérinaires', available: selectedTab !== 'free' },
                { label: 'Tableau de bord et statistiques', available: selectedTab !== 'free' },
              ])}
            </FeaturesList>
          </Box>
        </Box>
        {selectedTab === 'free' && (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: { xs: '92%', md: '70%' },
              maxWidth: 1000,
              mt: { xs: '30px', md: '35px' },
              mx: 'auto',
            }}
          >
            <Typography
              sx={{
                position: { xs: 'relative', md: 'absolute' },
                top: { xs: 0, md: -17 },
                left: { xs: 0, md: 10 },
                fontWeight: 700,
                fontSize: { xs: 18, md: 21.6 }, // ~1.2x
                color: colors.blue,
                m: 0,
                textAlign: { xs: 'center', md: 'left' },
                mb: { xs: 0.5, md: 0 },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                Offre gratuite & pourboires facultatifs
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                Offre gratuite
                <br />& pourboires facultatifs
              </Box>
            </Typography>
            <Box
              sx={{
                backgroundColor: '#D8EFFE',
                borderRadius: 2,
                px: { xs: 1.5, md: 2.5 },
                pb: { xs: 2, md: 2.5 },
                pt: { xs: 2, md: 4 },
                textAlign: 'left',
                color: theme.palette.secondary.main,
                width: '100%',
                mx: 'auto',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.82rem', md: '0.92rem' },
                  fontWeight: 500,
                  width: '100%',
                  mx: 'auto',
                  textAlign: 'center',
                  color: theme.palette.secondary.main,
                  lineHeight: { xs: 1.35, md: 1.25 },
                }}
              >
                Pour chaque demande d&apos;adoption, vos adoptants peuvent régler en ligne via un espace dédié.
                <br />
                <strong>
                  Paiement sécurisé avec{' '}
                  <Box
                    component="a"
                    href="https://stripe.com/fr"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    Stripe
                  </Box>
                  , 100% du montant reversé à l’association.
                </strong>
                <br />
                Les pourboires facultatifs aident à maintenir Petso gratuit pour les associations.
                <br />
                <br />
                <strong>
                  Pour activer les règlements en ligne et renseigner votre compte de versement, configurer votre{' '}
                  <Box
                    component="a"
                    href="https://stripe.com/fr"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ color: 'inherit', textDecoration: 'underline' }}
                  >
                    Stripe
                  </Box>{' '}
                  x Petso (~3&nbsp;min) et lancer une procédure d&apos;adoption sur Petso !
                </strong>
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 1.5, md: 2 }, width: '100%' }}>
              <StripeConnectButton
                content="Configurer mon Stripe Connect"
                color="primary"
                sx={{
                  textTransform: 'none',
                  fontWeight: 700,
                  borderRadius: '999px',
                  px: 3,
                  mx: 'auto',
                  display: 'block',
                }}
                fullWidth={false}
              />
            </Box>
          </Box>
        )}
        {selectedTab === 'premium' && (
          <Box
            sx={{
              position: 'relative',
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              width: { xs: '92%', md: '70%' },
              maxWidth: 1000,
              mt: { xs: '30px', md: '35px' },
              mx: 'auto',
            }}
          >
            <Typography
              sx={{
                position: { xs: 'relative', md: 'absolute' },
                top: { xs: 0, md: -17 },
                left: { xs: 0, md: 10 },
                fontWeight: 700,
                fontSize: { xs: 18, md: 21.6 }, // ~1.2x
                color: colors.blue,
                m: 0,
                textAlign: { xs: 'center', md: 'left' },
                mb: { xs: 0.5, md: 0 },
              }}
            >
              <Box component="span" sx={{ display: { xs: 'none', md: 'inline' } }}>
                Exploitez 100 % de Petso.
              </Box>
              <Box component="span" sx={{ display: { xs: 'inline', md: 'none' } }}>
                Exploitez 100 % de Petso.
              </Box>
            </Typography>
            <Box
              sx={{
                backgroundColor: '#D8EFFE',
                borderRadius: 2,
                px: { xs: 1.5, md: 2.5 },
                pb: { xs: 2, md: 2.5 },
                pt: { xs: 2, md: 4 },
                textAlign: 'left',
                color: theme.palette.secondary.main,
                width: '100%',
                mx: 'auto',
              }}
            >
              <Typography
                sx={{
                  fontSize: { xs: '0.82rem', md: '0.92rem' },
                  fontWeight: 500,
                  width: '100%',
                  mx: 'auto',
                  textAlign: 'center',
                  color: theme.palette.secondary.main,
                  lineHeight: { xs: 1.35, md: 1.25 },
                }}
              >
                Avec l’offre payante, activez les paiements en ligne, personnalisez vos modèles de contrats et
                certificats, générez vos documents automatiquement et récupérez les signatures des adoptants directement
                dans leur espace dédié.
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: { xs: 1.5, md: 2 }, width: '100%' }}>
              <ChoosePaymentPlanModal
                offerName="utile"
                prices={['48', '492']}
                priceIds={['price_1O4JKCKwIFnHkxNPL6rDRzL5', 'price_1O4JJlKwIFnHkxNPQY9EHNDk']}
                disabled={false}
              >
                {({ openModal }) => (
                  <StripeConnectButton
                    onClick={openModal}
                    content="Passer à l'offre utile"
                    color="primary"
                    sx={{
                      textTransform: 'none',
                      fontWeight: 700,
                      borderRadius: '999px',
                      px: 3,
                      mx: 'auto',
                      display: 'block',
                      width: 'auto',
                    }}
                    fullWidth={false}
                  />
                )}
              </ChoosePaymentPlanModal>
            </Box>
          </Box>
        )}
      </ContentCard>
    </Box>
  )
}

export default OfferTabs
